/**
 * Calendar-date helpers shared by all four apps.
 *
 * Postgres `date` columns (trip start/end, contract renewal, warranty expiry,
 * estimate valid_until, report_date) arrive as 'YYYY-MM-DD'. That is a calendar
 * day, not an instant: `new Date('2025-10-01')` is UTC midnight, which renders
 * as Sep 30 in every timezone west of Greenwich. Parse it as local midnight
 * instead. Timestamps (`timestamptz`, ISO strings with a time) keep their
 * instant.
 *
 * Every app's formatters go through `toLocalDate` so the rule lives once.
 * Guard: tests/sharedDates.test.ts runs these under US timezones.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

export function isDateOnly(value: string): boolean {
  return DATE_ONLY.test(value);
}

/** Parse 'YYYY-MM-DD' as local midnight. Returns null for anything else. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A database date or timestamp as a `Date`: a bare 'YYYY-MM-DD' is local
 * midnight, anything else is parsed as an instant. Null when empty or invalid.
 */
export function toLocalDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const dateOnly = parseDateOnly(value);
  if (dateOnly) return dateOnly;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local calendar date as 'YYYY-MM-DD' (never the UTC date `toISOString` gives). */
export function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole calendar days from `from` to `to` (positive when `to` is later). Each
 * side is reduced to its local calendar day first, so "today" against a DATE
 * counts days, not elapsed 24-hour periods. Null when either side is invalid.
 */
export function calendarDaysBetween(
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
): number | null {
  const a = toLocalDate(from);
  const b = toLocalDate(to);
  if (!a || !b) return null;
  const dayA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const dayB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((dayB - dayA) / MS_PER_DAY);
}

/** The local calendar day `days` after `value`, as 'YYYY-MM-DD'. */
export function addCalendarDays(value: string | Date, days: number): string | null {
  const d = toLocalDate(value);
  if (!d) return null;
  return localDateString(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
}
